import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useProjectContext } from "@contexts/projects";
import { fetchProjectTitleAnnulments } from "@contexts/projects/titleAnnulmentsApi";
import TitleAnnulmentsOffcanvas from "./TitleAnnulmentsOffcanvas";

// Abre a barra lateral "Títulos anulados na Ampli" de qualquer tela (card, indicador, menu de
// ações da obra). Montado no layout do dashboard. Fora do provider o contexto é inerte: o
// indicador continua renderizando e o clique simplesmente não faz nada (testes de tela).

const noop = () => {};
const INERT = {
  open: false,
  status: "idle",
  data: null,
  openOffcanvas: noop,
  closeOffcanvas: noop,
  load: noop,
  reload: noop,
};

const TitleAnnulmentsContext = createContext(INERT);

export const useTitleAnnulments = () => useContext(TitleAnnulmentsContext);

export const TitleAnnulmentsProvider = ({ children }) => {
  const { activeProject, companies } = useProjectContext();
  const erp = companies?.find((company) => company._id === activeProject?.company_id)?.erp;
  const projectId = activeProject?._id;
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState("idle");
  const [data, setData] = useState(null);
  const projectRef = useRef(projectId);
  projectRef.current = projectId;

  // Trocar de obra descarta a lista e fecha a barra.
  useEffect(() => {
    setOpen(false);
    setData(null);
    setStatus("idle");
  }, [projectId]);

  const load = useCallback(async ({ force = false } = {}) => {
    if (!projectId) return;
    const requested = projectId;
    setStatus((current) => (force && current === "ready" ? current : "loading"));
    const result = await fetchProjectTitleAnnulments(requested);
    if (projectRef.current !== requested) return;
    if (result?.ok && result.body) {
      setData(result.body);
      setStatus("ready");
    } else {
      setStatus("error");
    }
  }, [projectId]);

  const openOffcanvas = useCallback(() => {
    setOpen(true);
    load({ force: true });
  }, [load]);
  const closeOffcanvas = useCallback(() => setOpen(false), []);
  const reload = useCallback(() => load({ force: true }), [load]);

  const value = useMemo(() => ({
    open, status, data, openOffcanvas, closeOffcanvas, load, reload,
  }), [open, status, data, openOffcanvas, closeOffcanvas, load, reload]);

  return (
    <TitleAnnulmentsContext.Provider value={value}>
      {children}
      <TitleAnnulmentsOffcanvas
        open={open}
        status={status}
        data={data}
        projectId={projectId}
        project={activeProject}
        erp={erp}
        reload={reload}
        onClose={closeOffcanvas}
      />
    </TitleAnnulmentsContext.Provider>
  );
};

export default TitleAnnulmentsContext;
